import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';
import { ThemeService } from '../theme.service';
import { PortalSidebarComponent } from '../portal-sidebar.component';
import { ToolbarComponent } from '../../../../shared/components/toolbar/toolbar.component';
import { AdvisorBannerComponent } from '../../../../shared/components/advisor-banner/advisor-banner.component';

/**
 * Shell del portal: sidebar propio (CSS puro) + contenido.
 *
 * No usa `mat-sidenav-container`: su medición de márgenes en `ngDoCheck` entraba en
 * bucle síncrono y congelaba el render al pintar contenido con layout dinámico. Aquí
 * el layout es 100% CSS (sidebar `position: fixed` + `margin-left` del contenido, con
 * transiciones), así que colapsar o abrir el drawer nunca dispara medición en JS.
 */
@Component({
  selector: 'app-portal-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalSidebarComponent, ToolbarComponent, AdvisorBannerComponent],
  templateUrl: './portal-layout.component.html',
  styleUrls: ['./portal-layout.component.css'],
})
export class PortalLayoutComponent implements OnInit, OnDestroy {
  private static readonly STORAGE_KEY = 'portalSidebarCollapsed';
  // Mismo umbral que el breakpoint `mdx` de Tailwind (min-width: 991px), que
  // controla la visibilidad del toolbar/hamburger. Un solo umbral en el
  // shell evita cualquier zona muerta entre "hay sidebar fijo" y "hay hamburger".
  private static readonly DESKTOP_QUERY = '(min-width: 991px)';

  /** true en ≥991px: el sidebar es fijo y colapsable; en móvil es un drawer. */
  isDesktop = true;
  /** Rail colapsado (solo desktop); se persiste. */
  collapsed = false;
  /** Drawer abierto (solo móvil). */
  mobileOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly observer: BreakpointObserver,
    private readonly theme: ThemeService,
  ) {
    this.collapsed =
      localStorage.getItem(PortalLayoutComponent.STORAGE_KEY) === 'true';
  }

  ngOnInit(): void {
    this.theme.activate();
    this.observer
      .observe([PortalLayoutComponent.DESKTOP_QUERY])
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.isDesktop = res.matches;
        // Al volver a desktop el drawer móvil deja de tener sentido: ciérralo.
        if (this.isDesktop) this.mobileOpen = false;
      });
  }

  ngOnDestroy(): void {
    this.theme.deactivate();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(
      PortalLayoutComponent.STORAGE_KEY,
      String(this.collapsed),
    );
  }

  toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
  }

  closeMobile(): void {
    this.mobileOpen = false;
  }
}
